/**
 * Sequence Detail Page (DAG-based)
 * View and manage a sequence with branching step tree and enrollments
 */

import React, { useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getSequence,
  deleteSequence,
  activateSequence,
  pauseSequence,
  getSequenceEnrollments,
  fetchAllEnrollmentsForExport,
  getSequenceStepCounts,
  createSequenceStep,
  updateSequenceStep,
  deleteSequenceStepChecked,
  cancelEnrollment,
} from "../services/sequences.service";
import {
  listActiveGroups,
  type SubscriptionGroup,
} from "../services/subscriptionGroups.service";
import { formatDate, formatDateTime } from "../utils/formatters";
import { isSafeInternalPath } from "../utils/navigation";
import Button from "../components/common/Button";
import { downloadSequenceSummaryCsv } from "../utils/sequenceSummaryCsv";
import { downloadSequenceRecipientsCsv } from "../utils/sequenceRecipientsCsv";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Card from "../components/ui/Card";
import Badge from "../components/ui/Badge";
import Modal from "../components/ui/Modal";
import StepTreeView from "../components/sequences/StepTreeView";
import DeleteStepDialog, {
  type DeleteStepChoice,
} from "../components/sequences/DeleteStepDialog";
import ProductJourneyCatchup from "../components/sequences/ProductJourneyCatchup";
import MemberJourneyCatchup from "../components/sequences/MemberJourneyCatchup";
import { getMemberCatchupStartPlans } from "../services/memberJourneyCatchup.service";
import { useAuth } from "../contexts/AuthContext";
import { normalizeRole } from "../utils/roleUtils";
import StepEditor from "../components/sequences/StepEditor";
import {
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  PlayIcon,
  PauseIcon,
  BoltIcon,
  UserGroupIcon,
  ChartBarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CalendarDaysIcon,
  FunnelIcon,
  TagIcon,
} from "@heroicons/react/24/outline";
import type {
  SequenceWithAnalytics,
  SequenceStep,
  SequenceEnrollment,
  SequenceStatus,
  CreateSequenceStepInput,
  UpdateSequenceStepInput,
} from "../types/sequence.types";
import {
  PHASE_LABELS,
  EVALUATION_CADENCE_OPTIONS,
  TRIGGER_DATE_FIELD_OPTIONS,
} from "../types/sequence.types";
import type { QueryCriteria } from "../types/list.types";

type TabType = "steps" | "enrollments" | "analytics";

const SequenceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // R7 (2026-05-25): validate backTo to block open-redirect via crafted
  // ?backTo=javascript:... or //evil.com payloads.
  const [searchParams] = useSearchParams();
  const rawBackTo = searchParams.get("backTo");
  const backTo = isSafeInternalPath(rawBackTo) ? rawBackTo : null;
  const queryClient = useQueryClient();
  // Missed-welcome review is admin/superadmin only (the RPCs enforce the same).
  const { user } = useAuth();
  const isAdmin = ["superadmin", "admin"].includes(normalizeRole(user?.role));

  const [activeTab, setActiveTab] = useState<TabType>("steps");
  const [showStepModal, setShowStepModal] = useState(false);
  const [editingStep, setEditingStep] = useState<SequenceStep | null>(null);
  const [deletingStep, setDeletingStep] = useState<SequenceStep | null>(null);
  const [parentStepIdForNew, setParentStepIdForNew] = useState<string | null>(
    null,
  );
  const [isChildStep, setIsChildStep] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const {
    data: sequence,
    isLoading,
    error: _error,
  } = useQuery({
    queryKey: ["sequence", id],
    queryFn: () => getSequence(id!),
    enabled: !!id,
  });

  const { data: enrollments } = useQuery({
    queryKey: ["sequence-enrollments", id],
    queryFn: () => getSequenceEnrollments(id!),
    enabled: !!id && activeTab === "enrollments",
  });

  // "Review missed welcomes" is enabled per journey by migration (Defend member
  // journeys only). A load failure just hides the panel.
  const { data: memberCatchupPlans } = useQuery({
    queryKey: ["member-catchup-plans", id],
    queryFn: () => getMemberCatchupStartPlans(id!).catch(() => null),
    enabled:
      !!id &&
      isAdmin &&
      activeTab === "enrollments" &&
      sequence?.trigger_type === "member_signup",
  });

  // Active subscription categories — used only to resolve a friendly name for
  // the sequence's current subscription_group_id in the read-only overview.
  const { data: subscriptionGroups } = useQuery({
    queryKey: ["active-subscription-groups"],
    queryFn: () => listActiveGroups().catch(() => [] as SubscriptionGroup[]),
  });

  const { data: stepCounts } = useQuery({
    queryKey: ["sequence-step-counts", id],
    queryFn: () => getSequenceStepCounts(id!),
    enabled: !!id,
  });

  const [exporting, setExporting] = useState(false);
  const handleSummaryExport = () => {
    if (!sequence?.analytics || exporting) {
      return;
    }
    setExporting(true);
    try {
      downloadSequenceSummaryCsv(
        sequence.name,
        sequence.status,
        sequence.analytics,
        sequence.steps ?? [],
        stepCounts ?? new Map(),
      );
    } catch (err) {
      console.error("Sequence summary export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  // Status changes carry the updated_at this page is showing (re-audit
  // round 3): a stale page must not activate/pause over someone else's
  // newer state — the save is refused and we refetch so the operator sees
  // the current state next to the conflict message. ONE mutation keyed by
  // target status (re-audit round 4): each new attempt resets the previous
  // error, so a stale Resume failure can't outlive a later Pause (or mask
  // its genuine failure) in the banner below.
  const statusMutation = useMutation({
    mutationFn: (target: "active" | "paused") => {
      if (!sequence) {
        throw new Error("Sequence not loaded yet. Reload and try again.");
      }
      return target === "active"
        ? activateSequence(id!, sequence.updated_at)
        : pauseSequence(id!, sequence.updated_at);
    },
    // RETURN the invalidation promise (re-audit round 5): isPending then
    // stays true until the refreshed sequence/token has actually arrived,
    // so a quick second click can't fire with the stale token (repeat
    // refusal after a conflict, or a phantom conflict after a success).
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: ["sequence", id] }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteSequence(id!),
    onSuccess: () => {
      navigate("/sequences");
    },
  });

  const createStepMutation = useMutation({
    mutationFn: createSequenceStep,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sequence", id] });
    },
  });

  const updateStepMutation = useMutation({
    mutationFn: ({
      stepId,
      input,
    }: {
      stepId: string;
      input: UpdateSequenceStepInput;
    }) => updateSequenceStep(stepId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sequence", id] });
    },
  });

  // Both choices go through the server-checked delete (mig 1721): it carries the
  // steps the dialog showed under the step and is refused if that subtree changed,
  // so a stale view can never cascade-delete steps the user did not see.
  // "keep_children" deletes ONE step and re-parents its children (mig 1720).
  const deleteStepMutation = useMutation({
    mutationFn: ({
      stepId,
      choice,
      seenIds,
    }: {
      stepId: string;
      choice: DeleteStepChoice;
      seenIds: string[];
    }) =>
      deleteSequenceStepChecked(
        stepId,
        choice === "keep_children",
        seenIds,
      ).then(() => undefined),
    // Returned so mutateAsync (the dialog) waits for the refreshed tree before
    // closing; otherwise the next click can act on the old tree.
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["sequence", id] }),
    // A refusal (e.g. the subtree changed) refreshes the tree behind the dialog.
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ["sequence", id] });
    },
  });

  const getStatusBadgeVariant = (
    status: SequenceStatus,
  ): "success" | "warning" | "error" | "gray" => {
    switch (status) {
      case "active":
        return "success";
      case "paused":
        return "warning";
      case "archived":
        return "error";
      default:
        return "gray";
    }
  };

  const getCadenceLabel = (cadence?: string): string => {
    const option = EVALUATION_CADENCE_OPTIONS.find((c) => c.value === cadence);
    return option?.label || "Manual Only";
  };

  const getDateFieldLabel = (field?: string | null): string => {
    const option = TRIGGER_DATE_FIELD_OPTIONS.find((f) => f.value === field);
    return option?.label || field || "";
  };

  const summarizeConditions = (criteria?: QueryCriteria | null): string => {
    if (!criteria || !criteria.conditions || criteria.conditions.length === 0) {
      return "No filter conditions";
    }
    const count = criteria.conditions.length;
    return `${count} condition${count !== 1 ? "s" : ""} (${criteria.operator})`;
  };

  const handleAddRootStep = () => {
    setEditingStep(null);
    setParentStepIdForNew(null);
    setIsChildStep(false);
    setShowStepModal(true);
  };

  const handleAddBranch = (parentStepId: string) => {
    setEditingStep(null);
    setParentStepIdForNew(parentStepId);
    setIsChildStep(true);
    setShowStepModal(true);
  };

  const handleEditStep = (step: SequenceStep) => {
    setEditingStep(step);
    setIsChildStep(step.parent_step_id !== null);
    setShowStepModal(true);
  };

  const handleSaveStep = async (
    input: CreateSequenceStepInput | UpdateSequenceStepInput,
  ) => {
    if (editingStep) {
      await updateStepMutation.mutateAsync({
        stepId: editingStep.id,
        input: input as UpdateSequenceStepInput,
      });
    } else {
      await createStepMutation.mutateAsync(input as CreateSequenceStepInput);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" text="Loading sequence..." />
      </div>
    );
  }

  // Only lose the page when there is genuinely no data (re-audit round 4
  // Low): a background-refetch failure with cached sequence data must NOT
  // swap the page for the load-error view — that would hide the status-
  // change error banner during a network/backend blip. With cached data,
  // render normally and treat the query error as a refetch failure.
  if (!sequence) {
    return (
      <Card
        variant="outlined"
        padding="md"
        className="border-red-200 bg-red-50"
      >
        <p className="text-red-800">
          Error loading sequence. It may not exist or you don't have access.
        </p>
        <Button
          variant="secondary"
          onClick={() => navigate(backTo || "/sequences")}
          className="mt-4"
        >
          {backTo ? "Back to Contact" : "Back to Sequences"}
        </Button>
      </Card>
    );
  }

  const hasRootStep = sequence.steps && sequence.steps.length > 0;

  const tabs = [
    { id: "steps", label: "Steps", count: undefined },
    { id: "enrollments", label: "Enrollments", count: enrollments?.length },
    { id: "analytics", label: "Analytics" },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(backTo || "/sequences")}
            className="mt-1"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-gray-900">
                {sequence.name}
              </h1>
              <Badge variant={getStatusBadgeVariant(sequence.status)}>
                {sequence.status.charAt(0).toUpperCase() +
                  sequence.status.slice(1)}
              </Badge>
            </div>
            {sequence.description && (
              <p className="mt-2 text-gray-600">{sequence.description}</p>
            )}
            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <ClockIcon className="h-4 w-4" />
                {getCadenceLabel(sequence.evaluation_cadence)}
              </span>
              {sequence.trigger_conditions &&
                sequence.trigger_conditions.conditions?.length > 0 && (
                  <span className="flex items-center gap-1">
                    <FunnelIcon className="h-4 w-4" />
                    {summarizeConditions(
                      sequence.trigger_conditions as QueryCriteria,
                    )}
                  </span>
                )}
              {sequence.trigger_date_field && (
                <span className="flex items-center gap-1">
                  <CalendarDaysIcon className="h-4 w-4" />
                  {Math.abs(sequence.trigger_offset_days || 0)} days{" "}
                  {(sequence.trigger_offset_days || 0) >= 0
                    ? "before"
                    : "after"}{" "}
                  {getDateFieldLabel(sequence.trigger_date_field)}
                </span>
              )}
              {sequence.event && (
                <span className="flex items-center gap-1">
                  <BoltIcon className="h-4 w-4" />
                  Event: {sequence.event.title}
                </span>
              )}
              {sequence.list && (
                <span className="flex items-center gap-1">
                  <UserGroupIcon className="h-4 w-4" />
                  {sequence.list.name}
                </span>
              )}
              {sequence.subscription_group_id && (
                <span className="flex items-center gap-1">
                  <TagIcon className="h-4 w-4" />
                  {subscriptionGroups?.find(
                    (g) => g.id === sequence.subscription_group_id,
                  )?.name || "Subscription category"}
                </span>
              )}
              {sequence.campaign && (
                <span className="text-blue-600">
                  Campaign: {sequence.campaign.name}
                </span>
              )}
              {sequence.last_evaluated_at && (
                <span className="text-gray-400">
                  Last evaluated: {formatDateTime(sequence.last_evaluated_at)}
                </span>
              )}
              {sequence.next_evaluation_at && (
                <span className="text-gray-400">
                  Next: {formatDateTime(sequence.next_evaluation_at)}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {sequence.status === "draft" && (
            <Button
              variant="primary"
              onClick={() => statusMutation.mutate("active")}
              disabled={statusMutation.isPending}
              className="flex items-center gap-2"
            >
              <PlayIcon className="h-4 w-4" />
              Activate
            </Button>
          )}
          {sequence.status === "active" && (
            <Button
              variant="secondary"
              onClick={() => statusMutation.mutate("paused")}
              disabled={statusMutation.isPending}
              className="flex items-center gap-2"
            >
              <PauseIcon className="h-4 w-4" />
              Pause
            </Button>
          )}
          {sequence.status === "paused" && (
            <Button
              variant="primary"
              onClick={() => statusMutation.mutate("active")}
              disabled={statusMutation.isPending}
              className="flex items-center gap-2"
            >
              <PlayIcon className="h-4 w-4" />
              Resume
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={handleSummaryExport}
            disabled={exporting || !sequence.analytics}
            className="flex items-center gap-2"
            title="Download a CSV of this sequence's metrics and per-step funnel"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            {exporting ? "Exporting..." : "Summary Export"}
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate(`/sequences/${id}/edit`)}
            className="flex items-center gap-2"
          >
            <PencilIcon className="h-4 w-4" />
            Edit
          </Button>
          <Button
            variant="ghost"
            onClick={() => setShowDeleteConfirm(true)}
            className="flex items-center gap-2 text-red-600 hover:text-red-700"
          >
            <TrashIcon className="h-4 w-4" />
            Delete
          </Button>
        </div>
      </div>

      {/* Status-change conflict / failure (concurrency refusal surfaces here;
          the page has already refetched to the current state above) */}
      {statusMutation.isError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
          {statusMutation.error instanceof Error
            ? statusMutation.error.message
            : "Failed to change sequence status."}
        </div>
      )}

      {/* Analytics Summary */}
      {sequence.analytics && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card variant="outlined" padding="sm">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {sequence.analytics.total_enrolled}
              </div>
              <div className="text-xs text-gray-500">Total Enrolled</div>
            </div>
          </Card>
          <Card variant="outlined" padding="sm">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {sequence.analytics.active_count}
              </div>
              <div className="text-xs text-gray-500">Active</div>
            </div>
          </Card>
          <Card variant="outlined" padding="sm">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {sequence.analytics.completed_count}
              </div>
              <div className="text-xs text-gray-500">Completed</div>
            </div>
          </Card>
          <Card variant="outlined" padding="sm">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-600">
                {sequence.analytics.messages_sent}
              </div>
              <div className="text-xs text-gray-500">Messages Sent</div>
            </div>
          </Card>
          <Card variant="outlined" padding="sm">
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {sequence.analytics.messages_sent > 0
                  ? (
                      (sequence.analytics.emails_opened /
                        sequence.analytics.messages_sent) *
                      100
                    ).toFixed(1)
                  : 0}
                %
              </div>
              <div className="text-xs text-gray-500">Open Rate</div>
            </div>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              className={`
                py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2
                ${
                  activeTab === tab.id
                    ? "border-primary-500 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }
              `}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span
                  className={`
                  px-2 py-0.5 rounded-full text-xs
                  ${activeTab === tab.id ? "bg-primary-100 text-primary-600" : "bg-gray-100 text-gray-600"}
                `}
                >
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "steps" && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">Step Tree</h2>
            {!hasRootStep && (
              <Button
                variant="primary"
                size="sm"
                onClick={handleAddRootStep}
                className="flex items-center gap-2"
              >
                <PlusIcon className="h-4 w-4" />
                Add Root Step
              </Button>
            )}
          </div>

          <StepTreeView
            steps={sequence.steps || []}
            onAddStep={handleAddRootStep}
            onAddBranch={handleAddBranch}
            onEditStep={handleEditStep}
            onDeleteStep={(step) => setDeletingStep(step)}
            readOnly={sequence.status === "archived"}
            stepCounts={stepCounts}
          />
          <DeleteStepDialog
            step={deletingStep}
            onClose={() => setDeletingStep(null)}
            onConfirm={(step, choice, seenIds) =>
              deleteStepMutation.mutateAsync({
                stepId: step.id,
                choice,
                seenIds,
              })
            }
          />
        </div>
      )}

      {activeTab === "enrollments" &&
        sequence.trigger_type === "product_acquired" && (
          <ProductJourneyCatchup
            key={id}
            sequenceId={id!}
            active={sequence.status === "active"}
          />
        )}
      {activeTab === "enrollments" &&
        sequence.trigger_type === "member_signup" &&
        isAdmin &&
        !!memberCatchupPlans?.length && (
          <MemberJourneyCatchup
            key={id}
            sequenceId={id!}
            active={sequence.status === "active"}
            startPlans={memberCatchupPlans}
          />
        )}
      {activeTab === "enrollments" && (
        <EnrollmentsTab
          enrollments={enrollments || []}
          sequenceId={id!}
          sequenceName={sequence.name}
        />
      )}

      {activeTab === "analytics" && <AnalyticsTab sequence={sequence} />}

      {/* Step Editor Modal */}
      <StepEditor
        isOpen={showStepModal}
        onClose={() => {
          setShowStepModal(false);
          setEditingStep(null);
        }}
        onSave={handleSaveStep}
        sequenceId={id!}
        parentStepId={parentStepIdForNew}
        editingStep={editingStep}
        isChildStep={isChildStep}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <Modal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          title="Delete Sequence"
        >
          <div className="space-y-4">
            <p className="text-gray-600">
              Are you sure you want to delete "{sequence.name}"? This will also
              cancel all active enrollments. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="bg-red-600 hover:bg-red-700"
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete Sequence"}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

// Enrollments Tab Component
const EnrollmentsTab: React.FC<{
  enrollments: SequenceEnrollment[];
  sequenceName: string;
  sequenceId: string;
}> = ({ enrollments, sequenceId, sequenceName }) => {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const cancelMutation = useMutation({
    mutationFn: cancelEnrollment,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["sequence-enrollments", sequenceId],
      });
    },
  });

  const [exportingRecipients, setExportingRecipients] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // Exports EVERY enrollment, not the page the table is showing. The table's query caps at 1000
  // rows (PostgREST default) and this sequence had 1,465 — exporting what is on screen would hand
  // someone a short list while they are working out who was contacted.
  const handleRecipientExport = async () => {
    if (exportingRecipients) {
      return;
    }
    setExportingRecipients(true);
    setExportError(null);
    try {
      const rows = await fetchAllEnrollmentsForExport(sequenceId);
      downloadSequenceRecipientsCsv(sequenceName, rows);
    } catch (err) {
      setExportError(
        err instanceof Error
          ? `${err.message} Nothing was downloaded.`
          : "Export failed. Nothing was downloaded.",
      );
    } finally {
      setExportingRecipients(false);
    }
  };

  const filteredEnrollments = enrollments.filter(
    (e) => statusFilter === "all" || e.status === statusFilter,
  );

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "active":
        return <PlayIcon className="h-4 w-4 text-blue-500" />;
      case "completed":
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case "cancelled":
        return <XCircleIcon className="h-4 w-4 text-gray-400" />;
      case "failed":
        return <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />;
      case "paused":
        return <PauseIcon className="h-4 w-4 text-yellow-500" />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900">Enrollments</h2>
        <div className="flex flex-col items-end gap-1">
          <button
            type="button"
            onClick={handleRecipientExport}
            disabled={exportingRecipients}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {exportingRecipients ? "Exporting…" : "Export Recipients"}
          </button>
          {/*
            A refusal nobody can see is indistinguishable from the truncation it exists to prevent,
            so a failed export says so instead of only reaching console.error.
          */}
          {exportError && (
            <p
              role="alert"
              className="text-xs text-amber-700 max-w-xs text-right"
            >
              {exportError}
            </p>
          )}
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
        >
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="paused">Paused</option>
          <option value="cancelled">Cancelled</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      {filteredEnrollments.length > 0 ? (
        <Card variant="outlined" padding="none">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Current Step
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Phase
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Enrolled
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Next Action
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredEnrollments.map((enrollment) => (
                <tr key={enrollment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    {enrollment.contact ? (
                      <div>
                        <div className="font-medium text-gray-900">
                          {enrollment.contact.first_name}{" "}
                          {enrollment.contact.last_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {enrollment.contact.email ||
                            enrollment.contact.phone_number}
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-400">Unknown Contact</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(enrollment.status)}
                      <span className="capitalize">{enrollment.status}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {(
                      enrollment.current_step as unknown as {
                        name?: string;
                        step_type?: string;
                      }
                    )?.name ||
                      (
                        enrollment.current_step as unknown as {
                          step_type?: string;
                        }
                      )?.step_type ||
                      "-"}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {enrollment.status === "active"
                      ? PHASE_LABELS[enrollment.phase] || enrollment.phase
                      : "-"}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {formatDate(enrollment.enrolled_at)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {enrollment.next_action_at
                      ? formatDateTime(enrollment.next_action_at)
                      : "-"}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {enrollment.status === "active" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => cancelMutation.mutate(enrollment.id)}
                        disabled={cancelMutation.isPending}
                        className="text-red-600"
                      >
                        Cancel
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : (
        <Card variant="outlined" padding="lg" className="text-center">
          <UserGroupIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No enrollments yet.</p>
          <p className="text-gray-400 text-sm mt-1">
            Contacts will appear here when they're enrolled in this sequence.
          </p>
        </Card>
      )}
    </div>
  );
};

// Analytics Tab Component
const AnalyticsTab: React.FC<{ sequence: SequenceWithAnalytics }> = ({
  sequence,
}) => {
  const analytics = sequence.analytics;

  if (!analytics) {
    return (
      <Card variant="outlined" padding="lg" className="text-center">
        <ChartBarIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500">No analytics data available yet.</p>
      </Card>
    );
  }

  // Delivery Rate = REAL SendGrid "delivered" events / sent, from sequence_message_history.delivered_at
  // (get_sequence_analytics.messages_delivered). Delivered-webhook capture was repaired 2026-07-29 and
  // now lands on ~99% of recent sends, so this is the accurate delivery signal (reflects real bounces).
  // Caveat: sequences sent BEFORE the repair have unrecoverable delivery events and will read low, so we
  // also surface the "accepted" rate (sent - failed)/sent beneath it — older sequences aren't broken.
  const deliveryRate =
    analytics.messages_sent > 0
      ? (
          (analytics.messages_delivered / analytics.messages_sent) *
          100
        ).toFixed(1)
      : "0";
  const acceptedRate =
    analytics.messages_sent > 0
      ? (
          ((analytics.messages_sent - analytics.messages_failed) /
            analytics.messages_sent) *
          100
        ).toFixed(1)
      : "0";

  // Denominator is EMAILS sent, not messages sent. sequence_message_history records every step,
  // including 'wait', 'apply_tag' and 'remove_tag' rows which carry sent_at to mark completion, and
  // get_sequence_analytics counted all of them. GearFire read 6.9% against 35,743 "messages" when
  // only 13,121 were emails — the real rate is 18.8%. Fixed 2026-09-06 (migration -003).
  const openRate =
    analytics.emails_sent > 0
      ? ((analytics.emails_opened / analytics.emails_sent) * 100).toFixed(1)
      : "0";

  const clickRate =
    analytics.emails_opened > 0
      ? ((analytics.emails_clicked / analytics.emails_opened) * 100).toFixed(1)
      : "0";

  const completionRate =
    analytics.total_enrolled > 0
      ? ((analytics.completed_count / analytics.total_enrolled) * 100).toFixed(
          1,
        )
      : "0";

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-gray-900">
        Sequence Performance
      </h2>

      {/* Enrollment Funnel */}
      <Card variant="outlined" padding="md">
        <h3 className="font-medium text-gray-900 mb-4">Enrollment Funnel</h3>
        <div className="grid grid-cols-4 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-3xl font-bold text-blue-600">
              {analytics.total_enrolled}
            </div>
            <div className="text-sm text-gray-600">Total Enrolled</div>
          </div>
          <div className="text-center p-4 bg-yellow-50 rounded-lg">
            <div className="text-3xl font-bold text-yellow-600">
              {analytics.active_count}
            </div>
            <div className="text-sm text-gray-600">In Progress</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-3xl font-bold text-green-600">
              {analytics.completed_count}
            </div>
            <div className="text-sm text-gray-600">Completed</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-3xl font-bold text-gray-600">
              {completionRate}%
            </div>
            <div className="text-sm text-gray-600">Completion Rate</div>
          </div>
        </div>
      </Card>

      {/* Message Performance */}
      <Card variant="outlined" padding="md">
        <h3 className="font-medium text-gray-900 mb-4">Message Performance</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-2xl font-bold text-gray-900">
              {analytics.messages_sent}
            </div>
            <div className="text-sm text-gray-500">Messages Sent</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900">
              {deliveryRate}%
            </div>
            <div className="text-sm text-gray-500">Delivery Rate</div>
            <div className="text-xs text-gray-400">
              Accepted {acceptedRate}%
            </div>
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900">{openRate}%</div>
            <div className="text-sm text-gray-500">Open Rate</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900">{clickRate}%</div>
            <div className="text-sm text-gray-500">Click Rate</div>
          </div>
        </div>
      </Card>

      {/* Failures */}
      {analytics.messages_failed > 0 && (
        <Card
          variant="outlined"
          padding="md"
          className="border-red-200 bg-red-50"
        >
          <div className="flex items-center gap-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
            <div>
              <div className="font-medium text-red-900">
                {analytics.messages_failed} Failed Messages
              </div>
              <div className="text-sm text-red-700">
                Some messages could not be delivered. Check individual
                enrollments for details.
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default SequenceDetail;
